@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.enginemodule

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.example.project.service.CustomerModuleState
import org.example.project.service.LizenzService
import org.example.project.service.installed as customerInstalled
import org.example.project.service.purchased as customerPurchased
import org.example.project.service.ZustandManager
import org.example.project.utils.All

enum class ModulShopViewMode {
    SHOP,
    MINE
}

private fun compareModuleVersions(left: String, right: String): Int {
    val a = left.trim().ifBlank { "0.0.0" }.split(Regex("[^0-9A-Za-z]+"))
        .filter { it.isNotBlank() }
    val b = right.trim().ifBlank { "0.0.0" }.split(Regex("[^0-9A-Za-z]+"))
        .filter { it.isNotBlank() }
    val max = maxOf(a.size, b.size)
    for (index in 0 until max) {
        val av = a.getOrNull(index)
        val bv = b.getOrNull(index)
        if (av == null) return -1
        if (bv == null) return 1
        val an = av.toIntOrNull()
        val bn = bv.toIntOrNull()
        val cmp = when {
            an != null && bn != null -> an.compareTo(bn)
            else -> av.compareTo(bv)
        }
        if (cmp != 0) return cmp
    }
    return 0
}

private fun latestModulesById(entries: List<All>): List<All> = entries
    .filter { it.modulId > 0 }
    .groupBy { it.modulId }
    .values
    .mapNotNull { versions ->
        versions.maxWithOrNull { left, right ->
            compareModuleVersions(left.modulReferenz, right.modulReferenz)
        }
    }
    .sortedBy { it.modulId }

@Composable
fun ModulShop(
    viewMode: ModulShopViewMode = ModulShopViewMode.SHOP,
    readOnly: Boolean = false,
    showHeaderTabs: Boolean = true,
    showFeaturedHero: Boolean = true,
    detailResetKey: Int = 0,
    requestedDetailModuleKey: String? = null,
    detailOpenRequestKey: Int = 0,
    sideNavigationContent: (@Composable () -> Unit)? = null,
    heroNavigationContent: (@Composable () -> Unit)? = null,
    onDetailVisibilityChange: (Boolean) -> Unit = {}
) {
    val scope = rememberCoroutineScope()
    var modules by remember { mutableStateOf<List<All>>(emptyList()) }
    var customerModules by remember { mutableStateOf<Map<Int, CustomerModuleState>>(emptyMap()) }
    var detailModuleKey by remember { mutableStateOf<String?>(null) }
    var activeUserCount by remember { mutableStateOf(0) }
    var stageModuleKey by remember { mutableStateOf<String?>(null) }
    val moduleStates = remember { mutableStateMapOf<String, ModuleState>() }
    val progressMap = remember { mutableStateMapOf<String, Int>() }

    fun closeDetails() {
        detailModuleKey = null
    }

    suspend fun reloadModules() {
        modules = latestModulesById(ModuleApi.loadSystemModules())
        if (readOnly) {
            customerModules = emptyMap()
            activeUserCount = 0
        } else {
            customerModules = LizenzService.loadCustomerModuleStateMap()
            activeUserCount = LizenzService.loadActiveCustomerUserCount()
        }
    }

    LaunchedEffect(Unit) {
        reloadModules()
    }

    LaunchedEffect(viewMode) {
        while (true) {
            delay(2500)
            reloadModules()
        }
    }

    LaunchedEffect(detailResetKey) {
        if (detailResetKey != 0) closeDetails()
    }

    LaunchedEffect(detailModuleKey) {
        onDetailVisibilityChange(detailModuleKey != null)
    }

    fun setState(moduleKey: String, state: ModuleState) {
        moduleStates[moduleKey] = state
    }

    suspend fun persist(module: ShopModuleMeta, nextState: Int, applyLatestModuleData: Boolean = false, markPurchased: Boolean = false) {
        val savedModules = persistShopModuleState(
            module = module,
            nextState = nextState,
            applyLatestModuleData = applyLatestModuleData,
            markPurchased = markPurchased
        )
        LizenzService.applyCustomerModulesFromSync(savedModules.values.toList())
        customerModules = LizenzService.loadCustomerModuleStateMap()
        activeUserCount = LizenzService.loadActiveCustomerUserCount()
    }

    fun buyFlow(module: ShopModuleMeta) {
        if (!canInstallShopModule(module, moduleStates[module.key])) return
        scope.launch {
            setState(module.key, ModuleStateVal.PURCHASED)
            persist(
                module = module,
                nextState = ZustandManager.Modul.State.DEAKTIVIERT,
                markPurchased = true
            )
        }
    }

    fun installFlow(module: ShopModuleMeta, fromUpdate: Boolean = false) {
        stageModuleKey = module.key
        scope.launch {
            setState(module.key, ModuleStateVal.INSTALLING)
            for (value in 0..100 step 5) {
                progressMap[module.key] = value
                delay(160)
            }
            progressMap.remove(module.key)
            setState(module.key, ModuleStateVal.INSTALLED)
            val currentCustomerState = customerModules[module.moduleId]
            persist(
                module = module,
                nextState = ZustandManager.Modul.State.AKTIVIERT,
                applyLatestModuleData = fromUpdate || currentCustomerState?.customerInstalled != true,
                markPurchased = true
            )
            stageModuleKey = null
        }
    }

    fun deactivateFlow(module: ShopModuleMeta) {
        if (!canDeactivateShopModule(module, moduleStates[module.key])) return
        stageModuleKey = module.key
        scope.launch {
            setState(module.key, ModuleStateVal.UNINSTALLING)
            for (value in 0..100 step 5) {
                progressMap[module.key] = value
                delay(160)
            }
            progressMap.remove(module.key)
            setState(module.key, ModuleStateVal.DISABLED)
            persist(module = module, nextState = ZustandManager.Modul.State.DEAKTIVIERT)
            stageModuleKey = null
        }
    }

    fun activateFlow(module: ShopModuleMeta) {
        if (!canActivateShopModule(module)) return
        scope.launch {
            setState(module.key, ModuleStateVal.INSTALLED)
            persist(module = module, nextState = ZustandManager.Modul.State.AKTIVIERT)
        }
    }

    val allModules = modules.map { createShopMeta(it, customerModules[it.modulId], purchaseByExactVersion = viewMode == ModulShopViewMode.SHOP) }
    allModules.forEach { module ->
        if (module.key !in moduleStates || (moduleStates[module.key] != ModuleStateVal.INSTALLING && moduleStates[module.key] != ModuleStateVal.UNINSTALLING)) {
            moduleStates[module.key] = deriveShopModuleState(module)
        }
    }

    val visibleModules = when (viewMode) {
        ModulShopViewMode.SHOP -> allModules.filter { module ->
            module.globalState == ZustandManager.Modul.State.AKTIVIERT
        }
        ModulShopViewMode.MINE -> allModules.filter { module ->
            val customerState = customerModules[module.moduleId]
            customerState?.customerPurchased == true || customerState?.customerInstalled == true
        }
    }

    LaunchedEffect(detailOpenRequestKey, requestedDetailModuleKey, visibleModules) {
        val requestedKey = requestedDetailModuleKey?.takeIf { it.isNotBlank() } ?: return@LaunchedEffect
        visibleModules.firstOrNull { module -> module.key == requestedKey }?.let { module ->
            detailModuleKey = module.key
        }
    }

    val detailModule = detailModuleKey?.let { key -> visibleModules.firstOrNull { it.key == key } }
    val detailState = detailModule?.let { moduleStates[it.key] ?: ModuleStateVal.AVAILABLE }
    val stageModule = stageModuleKey?.let { key -> visibleModules.firstOrNull { it.key == key } ?: allModules.firstOrNull { it.key == key } }
    val stageState = stageModule?.let { moduleStates[it.key] }
    val showStage = stageModule != null && (stageState == ModuleStateVal.INSTALLING || stageState == ModuleStateVal.UNINSTALLING)

    ShopLayout(
        modules = visibleModules,
        featuredSource = if (heroNavigationContent != null) allModules.filter { it.globalState == ZustandManager.Modul.State.AKTIVIERT } else visibleModules,
        moduleStates = moduleStates,
        progressMap = progressMap,
        onInstall = { module ->
            when {
                viewMode == ModulShopViewMode.MINE -> installFlow(module)
                (moduleStates[module.key] ?: ModuleStateVal.AVAILABLE) == ModuleStateVal.PURCHASED -> installFlow(module)
                else -> buyFlow(module)
            }
        },
        onActivate = { activateFlow(it) },
        onDeactivate = { deactivateFlow(it) },
        onUpdate = { installFlow(it, fromUpdate = true) },
        onOpenDetails = { detailModuleKey = it.key },
        selectedDetailModule = detailModule,
        selectedDetailState = detailState,
        onCloseDetails = { closeDetails() },
        showHeaderTabs = showHeaderTabs,
        showFeaturedHero = showFeaturedHero,
        showOwnershipStagePanel = viewMode == ModulShopViewMode.MINE,
        showPurchaseOnly = viewMode == ModulShopViewMode.SHOP,
        readOnly = readOnly,
        emptyStateText = if (viewMode == ModulShopViewMode.MINE) "Du besitzt aktuell noch keine Module." else "Keine aktiven Module vorhanden.",
        sectionTitle = if (viewMode == ModulShopViewMode.MINE) "Meine Module" else "Module die dir gefallen könnten",
        featuredActiveUserCount = activeUserCount,
        sideNavigationContent = sideNavigationContent,
        heroNavigationContent = heroNavigationContent
    )

    val currentStageModule = stageModule
    val currentStageState = stageState
    if (
        currentStageModule != null &&
        (currentStageState == ModuleStateVal.INSTALLING || currentStageState == ModuleStateVal.UNINSTALLING)
    ) {
        ShopActionStage(
            module = currentStageModule,
            state = currentStageState,
            progress = progressMap[currentStageModule.key] ?: 0,
            onClose = { stageModuleKey = null }
        )
    }
}


